import { Alert, Button, Group, Select, Stack, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { IconMailForward, IconUnlink } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { usePermission } from "@how/app-kit";
import { ConfirmActionModal } from "@how/ui";
import {
  describeLoginError,
  useChurchLogins,
  useInviteToPortal,
  useLinkLogin,
  useUnlinkLogin,
} from "./login-queries";

export interface PortalLoginSectionProps {
  readonly personId: string;
  readonly personName: string;
  /** people.auth_user_id: the login this person is linked to, or null. */
  readonly authUserId: string | null;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Whether this person can sign in to the member portal as themselves, and the ways to change it (#136, ADR 0045).
 * Linking needs people.write; choosing among the church's logins also needs users.read (owner and administrator,
 * register D17); inviting a new address needs users.manage. The screen suggests no login: staff choose, because
 * matching by email could link a child through a parent's address (ADR 0045). The age rule is the database's.
 */
export function PortalLoginSection({ personId, personName, authUserId }: PortalLoginSectionProps) {
  const queryClient = useQueryClient();
  const canWritePeople = usePermission("people.write", { write: true });
  const canReadUsers = usePermission("users.read");
  const canInvite = usePermission("users.manage", { write: true });
  const mayLink = canWritePeople.data === true && canReadUsers.data === true;
  const mayInvite = canWritePeople.data === true && canInvite.data === true;
  const logins = useChurchLogins(canReadUsers.data === true);
  const link = useLinkLogin();
  const unlink = useUnlinkLogin();
  const invite = useInviteToPortal();
  const [chosen, setChosen] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const linkedLogin = logins.data?.find((login) => login.userId === authUserId);
  // Every login not linked to anyone yet, in the order the database gives (by email). Nothing is pre-selected.
  const options = useMemo(
    () =>
      (logins.data ?? [])
        .filter((login) => !login.linked)
        .map((login) => ({
          value: login.userId,
          label: login.status === "invited" ? `${login.email} (invited)` : login.email,
        })),
    [logins.data],
  );
  const refreshLogins = () => queryClient.invalidateQueries({ queryKey: ["church-logins"] });

  return (
    <Stack gap="xs">
      <Title order={4}>Portal login</Title>
      {authUserId !== null ? (
        <>
          <Text size="sm">
            {linkedLogin === undefined
              ? `${personName} can sign in to the member portal and see their own record.`
              : `${personName} signs in to the member portal as ${linkedLogin.email}.`}
          </Text>
          {canWritePeople.data === true ? (
            <Group>
              <Button
                variant="default"
                size="xs"
                leftSection={<IconUnlink size={14} />}
                onClick={() => {
                  setError(null);
                  setConfirmUnlink(true);
                }}
              >
                Unlink
              </Button>
            </Group>
          ) : null}
        </>
      ) : (
        <>
          <Text size="sm" c="dimmed">
            Not linked. {personName} cannot see their own record in the member portal.
          </Text>
          {mayLink ? (
            <Group align="flex-end" gap="xs" wrap="wrap">
              <Select
                label="Link an existing login"
                placeholder={
                  logins.isPending
                    ? "Loading the church's logins"
                    : options.length === 0
                      ? "Every login is linked already"
                      : "Choose a login"
                }
                searchable
                clearable
                data={options}
                value={chosen}
                onChange={setChosen}
                disabled={options.length === 0}
                w={{ base: "100%", sm: 280 }}
              />
              <Button
                size="sm"
                disabled={chosen === null}
                loading={link.isPending}
                onClick={() => {
                  if (chosen === null) return;
                  setError(null);
                  link.mutate(
                    { personId, userId: chosen },
                    {
                      onSuccess: () => {
                        setChosen(null);
                        void refreshLogins();
                        notifications.show({
                          color: "teal",
                          title: "Portal login linked",
                          message: `${personName} can now see their own record in the member portal.`,
                        });
                      },
                      onError: (e) => {
                        setError(describeLoginError(e));
                      },
                    },
                  );
                }}
              >
                Link
              </Button>
            </Group>
          ) : null}
          {mayInvite ? (
            <Group align="flex-end" gap="xs" wrap="wrap">
              <TextInput
                label="Or invite them to the portal"
                description="An email with a sign-in link. Nothing is sent if they are under the portal age."
                type="email"
                placeholder="their@email.address"
                value={email}
                onChange={(event) => {
                  const value = event.currentTarget.value;
                  setEmail(value);
                }}
                w={{ base: "100%", sm: 280 }}
              />
              <Button
                size="sm"
                variant="default"
                leftSection={<IconMailForward size={14} />}
                disabled={!EMAIL.test(email.trim())}
                loading={invite.isPending}
                onClick={() => {
                  setError(null);
                  invite.mutate(
                    { personId, email: email.trim() },
                    {
                      onSuccess: (result) => {
                        setEmail("");
                        void refreshLogins();
                        notifications.show({
                          color: result.personLinked ? "teal" : "orange",
                          title: "Invitation sent",
                          message: result.personLinked
                            ? `${personName} is linked to the invitation and sees their own record once they accept.`
                            : `The invitation went out, but ${personName} was not linked to it. Link them here once it appears in the list.`,
                        });
                      },
                      onError: (e) => {
                        setError(describeLoginError(e));
                      },
                    },
                  );
                }}
              >
                Invite
              </Button>
            </Group>
          ) : null}
        </>
      )}
      {error === null ? null : (
        <Alert color="red" variant="light" role="alert">
          {error}
        </Alert>
      )}
      <ConfirmActionModal
        opened={confirmUnlink}
        onClose={() => {
          setConfirmUnlink(false);
        }}
        title={`Unlink ${personName}'s portal login?`}
        message={`${personName} will no longer see their own record in the member portal. The login and its membership stay; you can link them again at any time. The change is on their timeline and in the audit log.`}
        confirmLabel="Unlink"
        danger
        loading={unlink.isPending}
        onConfirm={() => {
          unlink.mutate(
            { personId },
            {
              onSuccess: () => {
                setConfirmUnlink(false);
                void refreshLogins();
                // Unlinking said nothing when it worked; linking always said so (#177 review).
                notifications.show({
                  color: "teal",
                  title: "Portal login unlinked",
                  message: `${personName} no longer sees their own record in the member portal. The login stays, and can be linked again.`,
                });
              },
              onError: (e) => {
                setConfirmUnlink(false);
                setError(describeLoginError(e));
              },
            },
          );
        }}
      />
    </Stack>
  );
}
